import { useMutation, useQuery, useQueryClient } from 'react-query';

import { useSecurePortalFetcher } from '../../../../shared/hooks';
import {
  ApiError,
  PaginationParams,
  PaginationResponse,
  UseReactQueryOptions,
  UseReactQueryPaginationOptions,
} from '../../../../shared/models';
import { updateObjectInArray } from '../../../../shared/utils';
import { User } from '../../../users/models';
import { GetFolderContentResponse } from '../../folders/models';
import {
  BulkChangeDocumentStatusRequest,
  DocumentSearchResult,
  DocumentStatus,
  EmailPurpose,
  GetSharedDocumentResponse,
  ShareDocumentWithUserResponse,
} from '../models';
import { getStatusRequestSuffix } from '../utils';

export const useSharedDocumentQuery = (
  documentId: string,
  userId: string,
  options?: UseReactQueryOptions<GetSharedDocumentResponse>
) => {
  const fetcher = useSecurePortalFetcher();

  return useQuery(
    ['shared-document', documentId],
    async () => {
      const { data } = await fetcher<GetSharedDocumentResponse>(
        `/v1/documents/${documentId}/share`,
        {
          query: {
            userId,
          },
        }
      ).json();

      return data;
    },
    options
  );
};

export const useShareDocumentWithUserMutation = () => {
  const fetcher = useSecurePortalFetcher();

  return useMutation(
    async (body: {
      documentId: string;
      recipients: string[];
      emailPurpose: EmailPurpose;
      optionalMessage?: string;
      ccMyself: boolean;
    }) => {
      const { data } = await fetcher<ShareDocumentWithUserResponse>(
        `/v1/documents/${body.documentId}/share/email`,
        {
          method: 'POST',
          json: {
            recipients: body.recipients,
            emailPurpose: body.emailPurpose,
            optionalMessage: body.optionalMessage,
            ccMyself: body.ccMyself,
          },
        }
      ).json();

      return data;
    }
  );
};

export const useBulkChangeDocumentsStatusMutation = () => {
  const fetcher = useSecurePortalFetcher();

  return useMutation<void, ApiError, BulkChangeDocumentStatusRequest>(
    async ({ status, documentIds }) => {
      const suffix = getStatusRequestSuffix(status);

      await fetcher(`/v1/documents/${suffix}`, {
        method: 'PATCH',
        json: { documentIds },
      }).flush();
    }
  );
};

export const useChangeDocumentStatusMutation = () => {
  const fetcher = useSecurePortalFetcher();
  const queryClient = useQueryClient();

  return useMutation(
    async ({
      documentId,
      status,
    }: {
      status: DocumentStatus;
      documentId: string;
      parentFolderId: string;
    }) => {
      const suffix = getStatusRequestSuffix(status);

      await fetcher(`/v1/documents/${documentId}/${suffix}`, {
        method: 'PATCH',
      }).flush();
    },
    {
      onSuccess: (data, { documentId, parentFolderId, status }) => {
        // updated document can be a nested search results so all search queries have to be removed
        queryClient.removeQueries('documents');

        const folderContent =
          queryClient.getQueryData<GetFolderContentResponse>([
            'folder-content',
            parentFolderId,
          ]);

        if (folderContent) {
          const document = folderContent.documents.find(
            (d) => d.id === documentId
          );

          if (document) {
            queryClient.setQueryData<GetFolderContentResponse>(
              ['folder-content', parentFolderId],
              {
                ...folderContent,
                documents: updateObjectInArray(
                  folderContent.documents,
                  document,
                  {
                    status,
                  }
                ),
              }
            );
          }
        }
      },
    }
  );
};

export const useDeleteDocumentMutation = () => {
  const fetcher = useSecurePortalFetcher();

  return useMutation(async (body: { documentId: string }) => {
    await fetcher(`v1/documents/${body.documentId}/delete-mark`, {
      method: 'PATCH',
    }).flush();
  });
};

export const useMoveDocumentMutation = () => {
  const fetcher = useSecurePortalFetcher();
  return useMutation(
    async ({
      documentId,
      folderId,
    }: {
      documentId: string;
      folderId: string;
    }) => {
      const { data } = await fetcher(`/v1/documents/${documentId}/move`, {
        method: 'PATCH',
        json: { folderId },
      }).json();
      return data;
    }
  );
};

export const useDocumentsQuery = (
  params: PaginationParams & { folderId: string },
  options?: UseReactQueryPaginationOptions<
    DocumentSearchResult,
    { folderId: string }
  >
) => {
  const fetcher = useSecurePortalFetcher();

  return useQuery(
    ['documents', params],
    async () => {
      const { pageIndex, pageSize, search, sortBy, folderId } = params;
      const { data } = await fetcher<PaginationResponse<DocumentSearchResult>>(
        '/v1/documents',
        {
          query: {
            page: pageIndex,
            size: pageSize,
            query: search,
            sort: sortBy,
            folderId: folderId,
          },
        }
      ).json();

      return data;
    },
    options
  );
};

export const useFolderUsersQuery = (folderId: string, documentId: string) => {
  const fetcher = useSecurePortalFetcher();

  return useQuery(['folderUsers', folderId, documentId], async () => {
    const { data } = await fetcher<User[]>(
      `/v1/users/folder/${folderId}/document/${documentId}`
    ).json();

    return data;
  });
};
